import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MapView from '../../components/MapView';
import { getCurrentPosition } from '../../utils/geolocation';
import { useDriverStore } from '../../store/driverStore';
import { useRideStore } from '../../store/rideStore';
import { useAuthStore } from '../../store/authStore';
import { wsService } from '../../services/websocket';
import { locationUpdateService } from '../../services/locationUpdateService';
import { isActiveRide, normalizeRideStatus, RideStatus, canAcceptRide, isCompletedRide, isAssignedRide } from '../../constants/ride-status';
import { DRIVER_EVENTS } from '../../constants/websocket-events';
import { useAlertManager } from '../../components/AlertProvider';
import { getErrorMessage } from '../../services/errorHandler';
import { MaterialCommunityIcons } from '../../utils/icons';
import { Colors } from '../../theme/colors';
import './DriverHomeScreen.css';

const DriverHomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { showAlert, dismissAlert } = useAlertManager();
  const { status, stats, setOnline, updateLocation, getVerificationStatus, getDocuments, vehicles, getVehicles, getStats } = useDriverStore();
  const { currentRide, acceptRide, rejectRide, updateCurrentRide, getDriverActiveRide } = useRideStore();
  const { user } = useAuthStore();
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const locationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const processedRideOffersRef = useRef<Set<string>>(new Set());
  const currentRideOfferRef = useRef<string | null>(null);


  useEffect(() => {
    initializeScreen();

    return () => {
      locationUpdateService.stopTracking();
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  }, []); 


  useEffect(() => {

    if (currentRide && 
        currentRide.id && 
        currentRide.id !== '0') {
      const normalizedStatus = normalizeRideStatus(currentRide.status);

      if (isCompletedRide(normalizedStatus)) {
        console.log('useEffect: Ride is completed/cancelled, clearing currentRide');
        updateCurrentRide(null);
        locationUpdateService.stopTracking();
        return;
      }

      if (!isActiveRide(normalizedStatus) && !isAssignedRide(normalizedStatus)) {
        console.log('useEffect: Ride is not active or assigned, clearing currentRide. Status:', normalizedStatus);
        updateCurrentRide(null);
        locationUpdateService.stopTracking();
        return;
      }

      locationUpdateService.startTracking(currentRide.id.toString());
    } else {

      if (currentRide) {
        console.log('useEffect: Invalid ride data, clearing currentRide');
        updateCurrentRide(null);
      }
      locationUpdateService.stopTracking();
    }
  }, [currentRide?.id, currentRide?.status, updateCurrentRide]);

  useEffect(() => {
    const cleanup = startLocationTracking();
    return cleanup;
  }, [isOnline]);

  useEffect(() => {
    if (status) {
      setIsOnline(status.is_online || false);
      setLoading(false);
    }
  }, [status]);




  const refreshVerificationStatus = async () => {
    try {
      await getVerificationStatus();
    } catch (error) {
      console.error('Failed to refresh verification status:', error);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshVerificationStatus();
      await getDocuments();
      await getStats();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const initializeScreen = async () => {
    try {

      if (currentRide) {
        const normalizedStatus = normalizeRideStatus(currentRide.status);
        if (!currentRide.id || 
            currentRide.id === '0' || 
            !currentRide.ride_number || 
            currentRide.ride_number <= 0 ||
            isCompletedRide(normalizedStatus) ||
            !isActiveRide(normalizedStatus)) {
          console.log('Initializing: Clearing invalid currentRide');
          updateCurrentRide(null);
        }
      }

      await wsService.ensureConnection('driver');
      await getVerificationStatus();
      await getVehicles();
      await getStats();

      getCurrentPosition(
        (position: any) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setLocation(loc);

          if (isOnline) {
            updateLocation(loc.lat, loc.lng);
          }
        },
        (error: any) => {
          console.warn('Failed to get initial location:', error);
        },
        { enableHighAccuracy: true, timeout: 15000 },
      );



      const loadActiveRide = async () => {
        try {
          const ride = await getDriverActiveRide();

          if (ride && ride.id && ride.id !== '0') {
            const normalizedStatus = normalizeRideStatus(ride.status);

            if (isCompletedRide(normalizedStatus)) {
              console.log('Ride is completed/cancelled, clearing currentRide');
              updateCurrentRide(null);
            } else if (!isActiveRide(normalizedStatus) && !isAssignedRide(normalizedStatus)) {

              console.log('Ride is not active or assigned, clearing currentRide. Status:', normalizedStatus);
              updateCurrentRide(null);
            } else {

              console.log('✅ Setting currentRide:', ride.id, ride.ride_number || 'no number', normalizedStatus);
              updateCurrentRide(ride);
            }
          } else {

            console.log('No valid active ride found, clearing currentRide');
            updateCurrentRide(null);
          }
        } catch (error) {
          console.error('Failed to load active ride:', error);
          updateCurrentRide(null);
        }
      };
      loadActiveRide();

      const handleRideOffer = (data: any) => {
        const rideData = data.ride || data;
        const rideId = rideData.id || rideData.rideId || data.id || data.rideId;

        if (!rideId) {
          showAlert('Error', 'Invalid ride offer data received', [{ text: 'OK' }]);
          return;
        }

        const rideIdString = String(rideId);


        if (processedRideOffersRef.current.has(`cancelled_${rideIdString}`)) {
          console.log('Ride was cancelled, ignoring offer:', rideIdString);
          return;
        }


        const rideStatus = rideData.status || data.status;
        if (rideStatus === 'CANCELLED' || rideStatus === 'cancelled' || rideStatus === 7 || rideStatus === '7') {
          console.log('Ride offer received but ride is already cancelled:', rideIdString, 'Status:', rideStatus);
          processedRideOffersRef.current.add(`cancelled_${rideIdString}`);

          setTimeout(() => {
            processedRideOffersRef.current.delete(`cancelled_${rideIdString}`);
          }, 5000);
          dismissAlert();
          return;
        }



        if (processedRideOffersRef.current.has(rideIdString)) {
          console.log('Ride offer already processed, ignoring:', rideIdString);
          return; 
        }


        processedRideOffersRef.current.add(rideIdString);

        currentRideOfferRef.current = rideIdString;


        setTimeout(() => {
          processedRideOffersRef.current.delete(rideIdString);
          if (currentRideOfferRef.current === rideIdString) {
            currentRideOfferRef.current = null;
          }
        }, 5 * 60 * 1000);

        const rideVehicleTypeId = rideData.vehicleTypeId || rideData.vehicle_type_id || data.vehicleTypeId || data.vehicle_type_id;
        const driverVehicleTypeIds = vehicles
          .filter(v => v.is_active)
          .map(v => v.vehicle_type_id);

        if (rideVehicleTypeId && driverVehicleTypeIds.length > 0) {
          const hasMatchingVehicle = driverVehicleTypeIds.includes(rideVehicleTypeId);
          if (!hasMatchingVehicle) {
            showAlert(
              'Vehicle Type Mismatch',
              `This ride requires vehicle type ID: ${rideVehicleTypeId}, but your active vehicle(s) have type(s): ${driverVehicleTypeIds.join(', ')}. Please add a matching vehicle to accept this ride.`,
              [{ text: 'OK' }]
            );
            return;
          }
        }

        const rideNumber = rideData.ride_number || rideData.rideNumber || rideData.id || 'N/A';
        const vehicleTypeName = rideData.vehicleTypeName || rideData.vehicle_type_name || (rideVehicleTypeId ? `Type ${rideVehicleTypeId}` : '');
        const estimatedFare = rideData.estimatedFare || rideData.estimated_fare;
        const originAddress = rideData.originAddress || rideData.origin_address || rideData.start_point?.address;
        const destinationAddress = rideData.destinationAddress || rideData.destination_address || rideData.end_point?.address;

        const rideDetails = [
          `Ride #${rideNumber}`,
          vehicleTypeName ? `Vehicle Type: ${vehicleTypeName}` : '',
          estimatedFare ? `Estimated Fare: $${estimatedFare}` : '',
          originAddress ? `From: ${originAddress.substring(0, 50)}${originAddress.length > 50 ? '...' : ''}` : '',
          destinationAddress ? `To: ${destinationAddress.substring(0, 50)}${destinationAddress.length > 50 ? '...' : ''}` : '',
        ].filter(Boolean).join('\n');

        showAlert('New Ride Request', rideDetails, [
          {
            text: 'Reject',
            style: 'cancel',
            onPress: async () => {
              try {
                const rideIdString = String(rideId);

                processedRideOffersRef.current.delete(rideIdString);

                if (currentRideOfferRef.current === rideIdString) {
                  currentRideOfferRef.current = null;
                }
                await rejectRide(rideIdString, 'Driver rejected');
                if (wsService.isConnected()) {
                  wsService.emit('reject_ride', {
                    rideId: Number(rideId),
                    reason: 'Driver rejected',
                  });
                }
              } catch (error: any) {
                const errorMessage = getErrorMessage(error) || 'Failed to reject ride';
                showAlert('Error', errorMessage, [{ text: 'OK' }]);
              }
            },
          },
          {
            text: 'Accept',
            onPress: async () => {
              try {
                const rideIdString = String(rideId);

                processedRideOffersRef.current.delete(rideIdString);

                if (currentRideOfferRef.current === rideIdString) {
                  currentRideOfferRef.current = null;
                }
                await acceptRide(rideIdString);
                navigate(`/ride/${rideIdString}`);
              } catch (error: any) {
                const errorMessage = getErrorMessage(error) || 'Failed to accept ride';
                showAlert('Error', errorMessage, [{ text: 'OK' }]);

                processedRideOffersRef.current.delete(String(rideId));
              }
            },
          },
        ]);
      };

      const handleRideUpdated = (data: any) => {
        if (data && data.id) {
          const normalizedStatus = normalizeRideStatus(data.status);

          if (isCompletedRide(normalizedStatus)) {
            updateCurrentRide(null);

            if (window.location.pathname === '/active-ride') {
              navigate('/');
            }
          } else {
            updateCurrentRide(data);
            navigate(`/ride/${data.id}`);
          }
        } else {
          updateCurrentRide(null);
        }
      };

      const handleCurrentRide = (data: any) => {
        if (data) {
          const rideData = data.ride || data;
          const normalizedStatus = normalizeRideStatus(rideData.status);
          const currentDriverId = user?.id?.toString();
          const rideDriverId = rideData.driver_id?.toString() || rideData.driverId?.toString();


          if (isCompletedRide(normalizedStatus)) {
            updateCurrentRide(null);

            if (window.location.pathname === '/active-ride') {
              navigate('/');
            }
            return;
          }

          if (normalizedStatus === RideStatus.PENDING || normalizedStatus === RideStatus.SEARCHING_DRIVER) {
            handleRideOffer(rideData);
            return;
          }

          if (normalizedStatus === RideStatus.DRIVER_ASSIGNED) {
            if (!rideDriverId || (currentDriverId && rideDriverId !== currentDriverId)) {
              handleRideOffer(rideData);
              return;
            }

            const canAccept = canAcceptRide(normalizedStatus);
            if (canAccept) {
              handleRideOffer(rideData);
              return;
            }
          }

          updateCurrentRide(rideData);
          if (isActiveRide(normalizedStatus)) {
            navigate('/active-ride');
          }
        } else {

          updateCurrentRide(null);
        }
      };

      const handleRideAccepted = (data: any) => {
        if (data.ride) {
          const rideIdString = String(data.ride.id || data.ride.rideId || data.id || data.rideId);

          processedRideOffersRef.current.delete(rideIdString);
          updateCurrentRide(data.ride);
          navigate(`/ride/${data.ride.id}`);
        }
      };

      const handleRideAcceptError = (data: any) => {
        showAlert('Error', data.message || 'Failed to accept ride', [{ text: 'OK' }]);
      };

      const handleStatusUpdated = (data: any) => {
        setIsOnline(data.isOnline || false);
      };

      const handleRideCancelled = (data: any) => {
        const rideData = data.ride || data;
        const rideId = rideData.id || rideData.rideId || data.id || data.rideId;

        if (rideId) {
          const rideIdString = String(rideId);
          console.log('🚫 Ride cancelled event received:', rideIdString, data);


          processedRideOffersRef.current.delete(rideIdString);

          processedRideOffersRef.current.add(`cancelled_${rideIdString}`);



          dismissAlert();

          requestAnimationFrame(() => {
            dismissAlert();
            setTimeout(() => {
              dismissAlert();
              setTimeout(() => {
                dismissAlert();
                setTimeout(() => {
                  dismissAlert();
                  setTimeout(() => {
                    dismissAlert();
                  }, 100);
                }, 100);
              }, 100);
            }, 50);
          });



          setTimeout(() => {
            processedRideOffersRef.current.delete(`cancelled_${rideIdString}`);
          }, 30000);


          if (currentRide && (currentRide.id === rideIdString || currentRide.id?.toString() === rideIdString)) {
            console.log('Clearing currentRide because ride was cancelled:', rideIdString);
            updateCurrentRide(null);
          }


          if (window.location.pathname === '/active-ride' || window.location.pathname.includes(`/ride/${rideIdString}`)) {
            if (currentRide && (currentRide.id === rideIdString || currentRide.id?.toString() === rideIdString)) {
              console.log('Navigating back to home because ride was cancelled');
              setTimeout(() => {
                navigate('/');
              }, 500);
            }
          }
        } else {

          console.log('🚫 Ride cancelled event received without rideId, dismissing any active alerts');
          dismissAlert();
          setTimeout(() => dismissAlert(), 100);
        }
      };

      const handleRideOfferDismissed = (data: { rideId: string; reason?: string }) => {
        const rideIdString = String(data.rideId);
        console.log('🚫 Ride offer dismissed event received - CLOSING POPUP:', rideIdString, data.reason);


        const isCurrentPopup = currentRideOfferRef.current === rideIdString || 
                               currentRideOfferRef.current?.toString() === rideIdString ||
                               currentRideOfferRef.current === data.rideId ||
                               currentRideOfferRef.current?.toString() === data.rideId;


        processedRideOffersRef.current.delete(rideIdString);

        processedRideOffersRef.current.add(`cancelled_${rideIdString}`);


        if (isCurrentPopup || !currentRideOfferRef.current) {
          currentRideOfferRef.current = null;



          dismissAlert();

          requestAnimationFrame(() => {
            dismissAlert();
            setTimeout(() => {
              dismissAlert();
              setTimeout(() => {
                dismissAlert();
                setTimeout(() => {
                  dismissAlert();
                  setTimeout(() => {
                    dismissAlert();
                    console.log('✅ Popup should be closed now');
                  }, 100);
                }, 100);
              }, 100);
            }, 50);
          });
        }



        setTimeout(() => {
          processedRideOffersRef.current.delete(`cancelled_${rideIdString}`);
        }, 30000);
      };

      wsService.on(DRIVER_EVENTS.RIDE_OFFER, handleRideOffer);
      wsService.on(DRIVER_EVENTS.RIDE_OFFER_DISMISSED, handleRideOfferDismissed);
      wsService.on(DRIVER_EVENTS.RIDE_UPDATED, handleRideUpdated);
      wsService.on(DRIVER_EVENTS.RIDE_ASSIGNED, handleCurrentRide);
      wsService.on(DRIVER_EVENTS.RIDE_DRIVER_ASSIGNED, handleCurrentRide);
      wsService.on(DRIVER_EVENTS.CURRENT_RIDE, handleCurrentRide);
      wsService.on(DRIVER_EVENTS.RIDE_ACCEPTED, handleRideAccepted);
      wsService.on(DRIVER_EVENTS.RIDE_ACCEPT_ERROR, handleRideAcceptError);
      wsService.on(DRIVER_EVENTS.STATUS_UPDATED, handleStatusUpdated);
      wsService.on(DRIVER_EVENTS.RIDE_CANCELLED, handleRideCancelled);

      return () => {
        processedRideOffersRef.current.clear(); 
        wsService.off(DRIVER_EVENTS.RIDE_OFFER, handleRideOffer);
        wsService.off(DRIVER_EVENTS.RIDE_OFFER_DISMISSED, handleRideOfferDismissed);
        wsService.off(DRIVER_EVENTS.RIDE_UPDATED, handleRideUpdated);
        wsService.off(DRIVER_EVENTS.RIDE_ASSIGNED, handleCurrentRide);
        wsService.off(DRIVER_EVENTS.RIDE_DRIVER_ASSIGNED, handleCurrentRide);
        wsService.off(DRIVER_EVENTS.CURRENT_RIDE, handleCurrentRide);
        wsService.off(DRIVER_EVENTS.RIDE_ACCEPTED, handleRideAccepted);
        wsService.off(DRIVER_EVENTS.RIDE_ACCEPT_ERROR, handleRideAcceptError);
        wsService.off(DRIVER_EVENTS.STATUS_UPDATED, handleStatusUpdated);
        wsService.off(DRIVER_EVENTS.RIDE_CANCELLED, handleRideCancelled);
      };
    } catch (error) {
      console.error('Initialization error:', error);
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    getCurrentPosition(
      (position: any) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setLocation(loc);



        if (isOnline && !currentRide) {


          updateLocation(loc.lat, loc.lng);
        }


        if (isOnline && currentRide && currentRide.id) {
          locationUpdateService.sendLocationUpdate(
            loc.lat,
            loc.lng,
            position.coords.heading || undefined,
            position.coords.speed || undefined,
            currentRide.id.toString(),
          );
        }
      },
      (error: any) => {
        console.warn('Failed to get current location:', error);

      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const startLocationTracking = () => {


    locationIntervalRef.current = setInterval(() => {
      if (isOnline) {
        getCurrentLocation();
      }
    }, 15000); 

    return () => {
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  };

  const handleToggleOnline = async () => {
    const newStatus = !isOnline;

    if (newStatus && !status?.can_accept_rides) {
      const verificationStatus = status?.verification_status?.toLowerCase() || 'pending';
      let message = 'Please complete verification to go online.';

      if (verificationStatus === 'pending') {
        message = 'Your account is under verification. Please wait for admin approval before going online.';
      } else if (verificationStatus === 'rejected') {
        message = 'Your verification was rejected. Please upload new documents and wait for approval.';
      } else if ((status?.documents_approved || 0) < (status?.required_documents?.length || 3)) {
        const missing = (status?.required_documents?.length || 3) - (status?.documents_approved || 0);
        message = `Please upload and get approved ${missing} more document(s) to go online.`;
      } else if (!vehicles || vehicles.length === 0) {
        message = 'Please add at least one vehicle to go online.';
      }

      showAlert('Cannot Go Online', message, [{ text: 'OK' }]);
      return;
    }

    try {
      await setOnline(newStatus);
      setIsOnline(newStatus);


      if (wsService.isConnected()) {
        wsService.emit('update_status', {
          isOnline: newStatus,
        });
      }

      if (newStatus) {
        getCurrentPosition(
          (position: any) => {
            const loc = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setLocation(loc);
            updateLocation(loc.lat, loc.lng);

            if (currentRide && currentRide.id) {
              locationUpdateService.sendLocationUpdate(
                loc.lat,
                loc.lng,
                position.coords.heading || undefined,
                position.coords.speed || undefined,
                currentRide.id.toString(),
              );
            } else if (newStatus) {

              updateLocation(loc.lat, loc.lng);
            }
          },
          () => {
            if (location && currentRide && currentRide.id) {
              locationUpdateService.sendLocationUpdate(
                location.lat,
                location.lng,
                undefined,
                undefined,
                currentRide.id.toString(),
              );
            } else if (newStatus && location) {

              updateLocation(location.lat, location.lng);
            }
          },
          { enableHighAccuracy: true, timeout: 10000 },
        );
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      if (errorMessage.includes('active ride') || errorMessage.includes('Cannot go offline')) {
        showAlert(
          'Cannot Go Offline',
          'You cannot go offline while you have an active ride. Please complete or cancel the ride first.',
          [{ text: 'OK' }],
        );
      } else {
        showAlert('Error', errorMessage, [{ text: 'OK' }]);
      }
    }
  };

  if (loading || !status) {
    return (
      <div className="driver-home-loading">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading driver status...</p>
      </div>
    );
  }

  const verificationStatus = status.verification_status?.toLowerCase() || 'pending';
  const isVerified = verificationStatus === 'approved';
  const isPending = verificationStatus === 'pending';
  const isRejected = verificationStatus === 'rejected';


  const mapMarkers = location ? [
    {
      position: { lat: location.lat, lng: location.lng },
      title: 'Your Location',
      color: Colors.primary,
      icon: undefined, 
    },
  ] : [];


  if (!location) {
    return (
      <div className="driver-home-container">
        <div className="driver-home-header">
          <div className="header-content">
            <div className="header-left">
              <div className="logo-container">
                <span className="logo-text">OZIO</span>
              </div>
            </div>
            <div className="header-right">
              <button
                className="refresh-button"
                onClick={onRefresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <div className="spinner-small"></div>
                ) : (
                  <MaterialCommunityIcons name="refresh" size={24} color={Colors.primary} />
                )}
              </button>
              <button
                className="menu-button"
                onClick={() => navigate('/profile')}
              >
                <MaterialCommunityIcons name="menu" size={28} color={Colors.textPrimary} />
              </button>
            </div>
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: 'calc(100vh - 80px)',
          flexDirection: 'column',
          gap: '16px',
          padding: '20px'
        }}>
          <MaterialCommunityIcons name="map-marker-off" size={64} color={Colors.textSecondary} />
          <p style={{ fontSize: '18px', fontWeight: 600, color: Colors.textSecondary, margin: 0 }}>
            Getting your location...
          </p>
          <p style={{ fontSize: '14px', color: Colors.textSecondary, margin: 0, textAlign: 'center' }}>
            Please allow location access to see the map
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="driver-home-container">
      <MapView
        center={location}
        zoom={15}
        markers={mapMarkers}
      />

      <div className="driver-home-header">
        <div className="header-content">
          <div className="header-left">
            <div className="logo-container">
              <span className="logo-text">OZIO</span>
            </div>
          </div>
          <div className="header-right">
            <button
              className="refresh-button"
              onClick={onRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <div className="spinner-small"></div>
              ) : (
                <MaterialCommunityIcons name="refresh" size={24} color={Colors.primary} />
              )}
            </button>
            <button
              className="menu-button"
              onClick={() => navigate('/profile')}
            >
              <MaterialCommunityIcons name="menu" size={28} color={Colors.textPrimary} />
            </button>
          </div>
        </div>
      </div>

      <div className="driver-home-overlay">
        <div className="status-card">
          <div className="status-header">
            <div className="status-left">
              <div className={`status-indicator ${isOnline ? 'online' : ''}`} />
              <div className="status-text-container">
                <p className="status-text">{isOnline ? 'Online' : 'Offline'}</p>
                <p className="status-subtext">
                  {isOnline ? 'Ready to accept rides' : 'Not accepting rides'}
                </p>
              </div>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={isOnline}
                onChange={handleToggleOnline}
              />
              <span className="slider"></span>
            </label>
          </div>

          {!status.can_accept_rides && (
            <div className="warning-container">
              <MaterialCommunityIcons name="alert" size={20} color={Colors.warning} />
              <p className="warning-text">
                {isPending
                  ? 'Account verification pending. You cannot accept rides until approved.'
                  : isRejected
                  ? 'Verification rejected. Please upload new documents.'
                  : (status.documents_approved || 0) < (status.required_documents?.length || 3)
                  ? `Please upload and get approved ${(status.required_documents?.length || 3) - (status.documents_approved || 0)} more document(s) to go online.`
                  : !vehicles || vehicles.length === 0
                  ? 'Please add at least one vehicle to go online.'
                  : !isOnline
                  ? 'Turn on online status to accept rides.'
                  : 'Complete verification to go online'}
              </p>
            </div>
          )}

          {vehicles.length > 0 && (
            <div className="vehicle-type-container">
              <MaterialCommunityIcons name="car" size={16} color={Colors.textSecondary} />
              <p className="vehicle-type-text">
                Active Vehicle Type(s): {vehicles.filter(v => v.is_active).map(v => `Type ${v.vehicle_type_id}`).join(', ') || 'None'}
              </p>
            </div>
          )}
        </div>

        {isPending && (
          <div className="verification-banner pending-banner">
            <div className="banner-content">
              <div className="banner-icon-container">
                <MaterialCommunityIcons name="clock-outline" size={24} color={Colors.warning} />
              </div>
              <div className="banner-text-container">
                <h3 className="banner-title">Verification Pending</h3>
                <p className="banner-description">
                  Your documents are under review. You'll be able to accept rides once your account is verified by our admin team.
                </p>
              </div>
            </div>
            <button
              className="banner-button"
              onClick={() => navigate('/documents')}
            >
              <span>View Documents</span>
              <MaterialCommunityIcons name="chevron-right" size={18} color={Colors.white} />
            </button>
          </div>
        )}

        {isRejected && (
          <div className="verification-banner rejected-banner">
            <div className="banner-content">
              <div className="banner-icon-container">
                <MaterialCommunityIcons name="alert-circle" size={24} color={Colors.error} />
              </div>
              <div className="banner-text-container">
                <h3 className="banner-title">Verification Rejected</h3>
                <p className="banner-description">
                  Your verification was rejected. Please upload new documents and wait for admin approval.
                </p>
              </div>
            </div>
            <button
              className="banner-button rejected-button"
              onClick={() => navigate('/documents')}
            >
              <span>Upload Documents</span>
              <MaterialCommunityIcons name="upload" size={18} color={Colors.white} />
            </button>
          </div>
        )}

        {isVerified && (
          <div className="stats-container">
            <div className="stat-card">
              <MaterialCommunityIcons name="calendar-today" size={24} color={Colors.primary} />
              <p className="stat-value">{stats?.today_rides ?? 0}</p>
              <p className="stat-label">Today's Rides</p>
            </div>
            <div className="stat-card">
              <MaterialCommunityIcons name="cash" size={24} color={Colors.success} />
              <p className="stat-value">₼{stats?.today_earnings?.toFixed(2) ?? '0.00'}</p>
              <p className="stat-label">Today's Earnings</p>
            </div>
            <div className="stat-card">
              <MaterialCommunityIcons name="star" size={24} color={Colors.warning} />
              <p className="stat-value">{stats?.average_rating?.toFixed(1) ?? '0.0'}</p>
              <p className="stat-label">Rating</p>
            </div>
          </div>
        )}

        {currentRide && 
         currentRide.id && 
         currentRide.id !== '0' && 
         !isCompletedRide(normalizeRideStatus(currentRide.status)) &&
         (isActiveRide(normalizeRideStatus(currentRide.status)) || 
          isAssignedRide(normalizeRideStatus(currentRide.status))) ? (
          <button
            className="track-ride-button"
            onClick={() => {
              const normalizedStatus = normalizeRideStatus(currentRide.status);
              if (isActiveRide(normalizedStatus)) {
                navigate('/active-ride');
              } else {
                navigate(`/ride/${currentRide.id}`);
              }
            }}
          >
            <div className="track-ride-content">
              <div className="track-ride-icon-container">
                <MaterialCommunityIcons name="map-marker-path" size={28} color={Colors.white} />
              </div>
              <div className="track-ride-info">
                <p className="track-ride-title">Track Current Ride</p>
                <p className="track-ride-subtitle">
                  {currentRide.ride_number ? `Ride #${currentRide.ride_number}` : `Ride ${currentRide.id}`} - {normalizeRideStatus(currentRide.status)}
                </p>
              </div>
              <MaterialCommunityIcons name="chevron-right" size={24} color={Colors.white} />
            </div>
          </button>
        ) : (
          <button
            className="view-ride-button"
            onClick={async () => {
              try {
                const ride = await getDriverActiveRide();
                if (ride && ride.id) {
                  const normalizedStatus = normalizeRideStatus(ride.status);
                  // Update store before navigating
                  updateCurrentRide(ride);
                  // Small delay to ensure store is updated
                  setTimeout(() => {
                    if (isActiveRide(normalizedStatus)) {
                      navigate('/active-ride');
                    } else {
                      navigate(`/ride/${ride.id}`);
                    }
                  }, 200);
                } else {
                  showAlert('No Active Ride', 'You don\'t have any active ride at the moment.', [{ text: 'OK' }]);
                }
              } catch (error: any) {
                const errorMessage = getErrorMessage(error) || 'Failed to check for active ride';
                showAlert('Error', errorMessage, [{ text: 'OK' }]);
              }
            }}
          >
            <div className="view-ride-content">
              <div className="view-ride-icon-container">
                <MaterialCommunityIcons name="map-search" size={24} color={Colors.primary} />
              </div>
              <div className="view-ride-info">
                <p className="view-ride-title">View Current Ride</p>
                <p className="view-ride-subtitle">Check and manage your active ride</p>
              </div>
              <MaterialCommunityIcons name="chevron-right" size={20} color={Colors.textSecondary} />
            </div>
          </button>
        )}

        {isVerified && (
          <div className="quick-actions">
            <button
              className="action-button"
              onClick={() => navigate('/earnings')}
            >
              <div className="action-icon-container">
                <MaterialCommunityIcons name="wallet" size={24} color={Colors.primary} />
              </div>
              <span className="action-text">Earnings</span>
            </button>
            <button
              className="action-button"
              onClick={() => navigate('/vehicles')}
            >
              <div className="action-icon-container">
                <MaterialCommunityIcons name="car" size={24} color={Colors.primary} />
              </div>
              <span className="action-text">Vehicles</span>
            </button>
            <button
              className="action-button"
              onClick={() => navigate('/documents')}
            >
              <div className="action-icon-container">
                <MaterialCommunityIcons name="file-document" size={24} color={Colors.primary} />
              </div>
              <span className="action-text">Documents</span>
            </button>
            <button
              className="action-button"
              onClick={() => navigate('/profile')}
            >
              <div className="action-icon-container">
                <MaterialCommunityIcons name="account" size={24} color={Colors.primary} />
              </div>
              <span className="action-text">Profile</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverHomeScreen;

